'use client';

import { isNil } from 'es-toolkit';
import { graphql } from 'src/gql';

import type { ID } from '@savvy/utils/id';
import { USER_ROLE_ADVISOR, USER_ROLE_ASSISTANT, USER_ROLE_CLIENT, USER_ROLE_CSA } from '@savvy/constants/user-roles';
import { useAuth } from '@savvy/deprecated-shared/hooks/use-auth';
import { useInviteTokenHeaders } from '@savvy/deprecated-shared/hooks/use-invite-token-header';
import { useQueryWithErrorLogging } from '@savvy/graphql-hooks/useQueryWithErrorLogging';

// Query for CLIENT role: fetch branding through client's household advisor
const CLIENT_ORGANIZATION_BRANDING_QUERY = graphql(/* GraphQL */ `
  query ClientOrganizationBranding($clientId: ID!) {
    client(id: $clientId) {
      id
      household {
        id
        advisor {
          id
          organization {
            id
            name
            organizationBranding {
              id
              logoUrl
              displayName
              shortDisplayName
              email
              customOnboardingEmailIntro
              accentColor
              fontFamily
              fontSizeScale
              disclosureText
            }
          }
        }
      }
    }
  }
`);

// Query for ADVISOR/STAFF roles: fetch branding directly from advisor's organization
const ORGANIZATION_BRANDING_QUERY = graphql(/* GraphQL */ `
  query OrganizationBranding($advisorId: ID!) {
    advisor(id: $advisorId) {
      id
      organization {
        id
        name
        organizationBranding {
          id
          logoUrl
          displayName
          shortDisplayName
          email
          customOnboardingEmailIntro
          accentColor
          fontFamily
          fontSizeScale
          disclosureText
          verifiedSendingDomain
          sendingFromEmail
        }
      }
    }
  }
`);

type OrganizationBranding = {
  id?: ID;
  logoUrl?: string | null;
  displayName?: string | null;
  shortDisplayName?: string | null;
  organizationId?: string;
  organizationName?: string;
  email?: string | null;
  customOnboardingEmailIntro?: string | null;
  accentColor?: string | null;
  fontFamily?: string | null;
  fontSizeScale?: string | null;
  disclosureText?: string | null;
  // Only selected by the advisor-flow query; undefined under the client flow.
  verifiedSendingDomain?: string | null;
  sendingFromEmail?: string | null;
};

export type UseOrganizationBrandingReturn = {
  branding: OrganizationBranding | null;
  loading: boolean;
  error: Error | null;
  organizationId: string | null;
  organizationName: string | null;
};

export type UseOrganizationBrandingParams = {
  /**
   * Optional client user ID. Used when advisors/staff are viewing a specific client's page.
   * If provided, fetches branding through that client's household advisor.
   * If not provided:
   * - For CLIENTs: uses their own ID
   * - For ADVISOR/STAFF: uses their own organization
   */
  readonly clientUserId?: ID;
};

/**
 * Hook to fetch organization branding.
 *
 * Supports two flows:
 * 1. CLIENT role: Client → Household → Advisor → Organization → OrganizationBranding
 * 2. ADVISOR/STAFF roles: Advisor → Organization → OrganizationBranding
 *
 * Usage:
 * - Client viewing own pages: `useOrganizationBranding()` - no params needed
 * - Advisor/staff viewing client pages: `useOrganizationBranding({ clientUserId: '123' })`
 * - Advisor/staff viewing own pages: `useOrganizationBranding()` - no params needed
 */
export function useOrganizationBranding(params?: UseOrganizationBrandingParams): UseOrganizationBrandingReturn {
  const { user } = useAuth();
  const { clientUserId } = params ?? {};
  const headers = useInviteTokenHeaders();

  const userRole = user?.role;

  const isClient = userRole === USER_ROLE_CLIENT;
  const isAdvisor = userRole === USER_ROLE_ADVISOR;
  const isAssistant = userRole === USER_ROLE_ASSISTANT;
  const isCsa = userRole === USER_ROLE_CSA;

  const isAdvisorOrStaff = isAdvisor || isAssistant || isCsa;

  // CLIENT FLOW: Use when clientUserId is provided and user is a CLIENT
  const shouldUseClientFlow = !isNil(clientUserId) || isClient;
  const targetClientId = clientUserId ?? (isClient ? user?.id : null);
  const targetClientIdString = !isNil(targetClientId) ? String(targetClientId) : '';

  const clientFlowQuery = useQueryWithErrorLogging(CLIENT_ORGANIZATION_BRANDING_QUERY, {
    variables: {
      clientId: targetClientIdString,
    },
    context: { headers },
    // Don't skip if we have a targetClientId - the query will use invite token headers if not authenticated
    skip: !shouldUseClientFlow || isNil(targetClientId),
    errorPolicy: 'all',
  });

  const shouldUseAdvisorFlow = !shouldUseClientFlow && isAdvisorOrStaff;

  const advisorFlowQuery = useQueryWithErrorLogging(ORGANIZATION_BRANDING_QUERY, {
    variables: {
      advisorId: user?.id ?? '',
    },
    context: { headers },
    skip: !shouldUseAdvisorFlow || isNil(user?.id),
    errorPolicy: 'all',
  });

  // Select the active query result
  const activeQuery = shouldUseClientFlow ? clientFlowQuery : advisorFlowQuery;
  const { data, loading, error } = activeQuery;

  // Extract branding from the appropriate query response
  let branding: OrganizationBranding | null = null;
  let organizationId: string | null = null;
  let organizationName: string | null = null;

  if (!isNil(data)) {
    let organization:
      | {
          id: string;
          name: string;
          organizationBranding?: OrganizationBranding | null;
        }
      | null
      | undefined;

    // Extract organization based on query type
    if (shouldUseClientFlow && 'client' in data) {
      organization = data.client.household.advisor?.organization;
    } else if (shouldUseAdvisorFlow && 'advisor' in data) {
      organization = data.advisor.organization;
    }

    // Always extract organization info if available
    if (!isNil(organization)) {
      organizationId = organization.id;
      organizationName = organization.name;
    }

    const brandingData = organization?.organizationBranding;

    if (!isNil(brandingData) && !isNil(organization)) {
      branding = {
        id: brandingData.id,
        logoUrl: brandingData.logoUrl,
        displayName: brandingData.displayName,
        shortDisplayName: brandingData.shortDisplayName ?? null,
        email: brandingData.email ?? null,
        customOnboardingEmailIntro: brandingData.customOnboardingEmailIntro ?? null,
        accentColor: brandingData.accentColor ?? null,
        fontFamily: brandingData.fontFamily ?? null,
        fontSizeScale: brandingData.fontSizeScale ?? null,
        disclosureText: brandingData.disclosureText ?? null,
        // Advisor flow only. Coalescing these to null under the client flow, whose
        // query doesn't select them, would report "sends on Savvy's shared domain"
        // for an org that has its own — so leave them undefined to mean "not asked for".
        verifiedSendingDomain: shouldUseAdvisorFlow ? (brandingData.verifiedSendingDomain ?? null) : undefined,
        sendingFromEmail: shouldUseAdvisorFlow ? (brandingData.sendingFromEmail ?? null) : undefined,
        organizationId: organization.id,
        organizationName: organization.name,
      };
    }
  }

  return {
    branding,
    loading,
    error: error ?? null,
    organizationId,
    organizationName,
  };
}
